import {
  filterVisibleFlowActions,
  hasCompletedLicenseVerification,
  isExpertWaitState,
  isLicenseWaitState,
  resolveStateName
} from './flowActionVisibilityHelper';

export const FLOW_PHASE = {
  LICENSE_READY_START: 'license_ready_start',
  LICENSE_WAIT: 'license_wait',
  LICENSE_READY_SEND: 'license_ready_send',
  EXPERT_WAIT: 'expert_wait',
  IN_PROCESS: 'in_process',
  REJECTED: 'rejected',
  PUBLISHED: 'published',
  OTHER: 'other'
};

const normalize = (value = '') => String(value || '').trim().toLowerCase();

const isPublishedState = (name = '') => {
  const normalized = normalize(name);
  return normalized === 'publicado' || normalized === 'published';
};

const isRejectedState = (name = '') => {
  const normalized = normalize(name);
  return normalized === 'rechazado' || normalized === 'rejected';
};

export const resolveRecordFlowPhase = ({
  record,
  flowStates = [],
  historyEvents = [],
  roleKey = '',
  moduleProcess = '',
  licenseEnabled = false
} = {}) => {
  const stateName = resolveStateName(record?.state_catalog_id, flowStates);

  if (isPublishedState(stateName)) {
    return FLOW_PHASE.PUBLISHED;
  }
  if (isRejectedState(stateName)) {
    return FLOW_PHASE.REJECTED;
  }
  if (isLicenseWaitState(stateName)) {
    return FLOW_PHASE.LICENSE_WAIT;
  }
  if (isExpertWaitState(stateName)) {
    return FLOW_PHASE.EXPERT_WAIT;
  }

  if (roleKey === 'validator' && licenseEnabled) {
    const licenseCompleted = hasCompletedLicenseVerification(historyEvents);
    return licenseCompleted ? FLOW_PHASE.LICENSE_READY_SEND : FLOW_PHASE.LICENSE_READY_START;
  }

  if (roleKey === 'editor') {
    return FLOW_PHASE.IN_PROCESS;
  }

  return FLOW_PHASE.IN_PROCESS;
};

export const getFlowPhaseLabel = (phase, t) => {
  const labels = {
    [FLOW_PHASE.LICENSE_READY_START]: t(
      'flow.mass.phase-license-start',
      'Pendientes de iniciar verificación de licencia'
    ),
    [FLOW_PHASE.LICENSE_WAIT]: t(
      'flow.mass.phase-license-wait',
      'En verificación de licencia'
    ),
    [FLOW_PHASE.LICENSE_READY_SEND]: t(
      'flow.mass.phase-license-send',
      'Listos para enviar a Editor'
    ),
    [FLOW_PHASE.EXPERT_WAIT]: t(
      'flow.mass.phase-expert-wait',
      'En consulta a experto'
    ),
    [FLOW_PHASE.IN_PROCESS]: t('flow.mass.phase-in-process', 'En Proceso'),
    [FLOW_PHASE.REJECTED]: t('flow.mass.phase-rejected', 'Rechazados'),
    [FLOW_PHASE.PUBLISHED]: t('flow.mass.phase-published', 'Publicados'),
    [FLOW_PHASE.OTHER]: t('flow.mass.phase-other', 'Otro estado')
  };
  return labels[phase] || labels[FLOW_PHASE.OTHER];
};

export const summarizeMassSelection = ({
  records = [],
  flowStates = [],
  historiesById = {},
  roleKey = '',
  moduleProcess = '',
  licenseEnabled = false,
  t = (key, fallback) => fallback || key
} = {}) => {
  const groupsMap = new Map();

  (records || []).forEach((record) => {
    const phase = resolveRecordFlowPhase({
      record,
      flowStates,
      historyEvents: historiesById[Number(record.id)] || historiesById[record.id] || [],
      roleKey,
      moduleProcess,
      licenseEnabled
    });
    const current = groupsMap.get(phase) || {
      phase,
      label: getFlowPhaseLabel(phase, t),
      count: 0,
      ids: []
    };
    current.count += 1;
    current.ids.push(record.id);
    groupsMap.set(phase, current);
  });

  const groups = Array.from(groupsMap.values()).sort((left, right) => right.count - left.count);

  return {
    mixed: groups.length > 1,
    total: records.length,
    groups,
    phase: groups.length === 1 ? groups[0].phase : null
  };
};

export const resolveMassVisibleActions = ({
  actions = [],
  records = [],
  flowStates = [],
  historiesById = {},
  moduleProcess,
  expertConsultEnabled,
  canOperate,
  mixed = false,
  roleKey = '',
  coordinatorApprovalEnabled = false,
  subjectKind = ''
} = {}) => {
  if (mixed || !records.length) {
    return [];
  }

  return records.reduce((intersection, record, index) => {
    const visible = filterVisibleFlowActions({
      actions,
      record,
      flowStates,
      moduleProcess,
      expertConsultEnabled,
      canOperate,
      licenseCompleted: hasCompletedLicenseVerification(historiesById[Number(record.id)] || historiesById[record.id] || []),
      roleKey,
      coordinatorApprovalEnabled,
      subjectKind: record?.subject_kind || subjectKind
    });
    const values = new Set(visible.map((action) => action.value));
    if (index === 0) {
      return visible;
    }
    return intersection.filter((action) => values.has(action.value));
  }, []);
};
